import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLButtonElement} [overflowTrigger] - The trigger for links moved into the overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#handleKeyDown);
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener('pointerdown', this.#handleDocumentPointerDown);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * @param {PointerEvent} event
   */
  #handleDocumentPointerDown = (event) => {
    if (!this.#state.activeItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#deactivate();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (!(event.target instanceof Element)) return;

    if (this.#handleDropdownLinkKey(event)) return;

    const menuItem = findMenuItem(event.target);
    if (!menuItem) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#deactivate(menuItem);
      menuItem.focus();
      return;
    }

    const submenu = findSubmenu(menuItem);
    if (!submenu) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#focusDropdownLink(submenu, 'first');
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#focusDropdownLink(submenu, 'last');
      return;
    }

    if (event.key !== ' ' && event.key !== 'Enter') return;

    event.preventDefault();

    if (menuItem.ariaExpanded === 'true') {
      this.#deactivate(menuItem);
    } else {
      this.activate(event);
    }
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Toggle a submenu from an explicit click on its top-level trigger.
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    const listItem = item.closest('.menu-list__list-item');
    const opensOverflowMenu = listItem?.slot === 'overflow' || event.target.closest('[slot="more"]');
    if (!findSubmenu(item) && !opensOverflowMenu) return;

    event.preventDefault();

    if (item === this.#state.activeItem && item.ariaExpanded === 'true') {
      this.#deactivate(item);
      return;
    }

    this.activate(event);
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {MouseEvent | KeyboardEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    this.#activateFromTarget(event.target);
  };

  /**
   * Activate a menu item from an event target.
   * @param {Element} target
   */
  #activateFromTarget(target) {
    if (!this.headerComponent) return;

    let item = findMenuItem(target);

    if (!item || item == this.#state.activeItem) return;

    const menuListItem = item.closest('.menu-list__list-item');
    const isDefaultSlot = menuListItem?.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.refs.overflowTrigger?.setAttribute('aria-expanded', (!isDefaultSlot).toString());

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);
    const isDropdownSubmenu = submenu?.classList.contains('menu-list__submenu--dropdown') ?? false;

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
    }

    if (submenu && !isDropdownSubmenu) {
      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = isDropdownSubmenu ? 0 : submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot && !isDropdownSubmenu) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    this.refs.overflowTrigger?.setAttribute('aria-expanded', 'false');

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * @param {HTMLElement} submenu
   * @param {'first' | 'last'} position
   */
  #focusDropdownLink(submenu, position) {
    const links = submenu.querySelectorAll('a[href]');
    const link = position === 'first' ? links[0] : links[links.length - 1];
    if (link instanceof HTMLElement) link.focus();
  }

  /**
   * @param {KeyboardEvent} event
   * @returns {boolean}
   */
  #handleDropdownLinkKey(event) {
    if (!(event.target instanceof HTMLElement)) return false;

    const submenu = event.target.closest('.menu-list__submenu--dropdown');
    if (!(submenu instanceof HTMLElement)) return false;

    const links = [...submenu.querySelectorAll('a[href]')].filter((link) => link instanceof HTMLElement);
    const currentIndex = links.indexOf(event.target);
    if (currentIndex === -1) return false;

    if (event.key === 'Escape') {
      event.preventDefault();
      const activeItem = this.#state.activeItem;
      this.#deactivate(activeItem);
      activeItem?.focus();
      return true;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return false;

    event.preventDefault();
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (currentIndex + direction + links.length) % links.length;
    const nextLink = links[nextIndex];
    if (nextLink instanceof HTMLElement) nextLink.focus();
    return true;
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreItem = element.closest('[slot="more"]');
  if (moreItem) {
    // Use the first overflowing menu item to control the shared overflow panel.
    return findMenuItem(moreItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  const closestMenuItem = element.closest('[ref="menuitem"]');
  if (closestMenuItem instanceof HTMLElement) return closestMenuItem;

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
